import type { GeolocationAvailabilityType, LeafletGeolocationApiModel } from '@/types/GeolocationModel'

export type GeolocationCodecResult = {
  geolocations: LeafletGeolocationApiModel[]
  invalidLines: string[]
}

const NUMBER_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/

function number(value: string): number | null {
  const trimmed = value.trim()
  if (!NUMBER_PATTERN.test(trimmed)) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

function coordinates(parts: string[]): Pick<LeafletGeolocationApiModel, 'lat' | 'lng' | 'radius'> | null {
  const lat = number(parts[0] ?? '')
  const lng = number(parts[1] ?? '')
  const radius = number(parts[2] ?? '')
  if (lat === null || lng === null || radius === null) return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180 || !Number.isInteger(radius) || radius <= 0) return null
  return { lat, lng, radius }
}

function lines(text: string): string[] {
  return text
    .split(/\n|(?<=\d)\s+(?=[+-]?(?:\d+(?:\.\d*)?|\.\d+)\s*,)/)
    .map(line => line.trim())
    .filter(Boolean)
}

function formatBase(value: LeafletGeolocationApiModel): string {
  return `${value.lat},${value.lng},${value.radius}`
}

export const separatedGeolocationCodec = {
  parse(text: string, availabilityType: GeolocationAvailabilityType): GeolocationCodecResult {
    return lines(text).reduce<GeolocationCodecResult>(
      (result, line) => {
        const parts = line.split(',')
        const value = coordinates(parts)
        if (!value) result.invalidLines.push(line)
        else {
          const name = parts.slice(3).join(',').trim()
          result.geolocations.push({ ...value, availabilityType, ...(name ? { name } : {}) })
        }
        return result
      },
      { geolocations: [], invalidLines: [] }
    )
  },
  format(values: readonly LeafletGeolocationApiModel[], availabilityType: GeolocationAvailabilityType): string {
    return values
      .filter(value => value.availabilityType === availabilityType)
      .map(value => `${formatBase(value)}${value.name ? `,${value.name}` : ''}`)
      .join('\n')
  }
}

export const legacyCombinedGeolocationCodec = {
  parse(text: string): GeolocationCodecResult {
    return lines(text).reduce<GeolocationCodecResult>(
      (result, line) => {
        const parts = line.split(',')
        const value = coordinates(parts)
        const availabilityType = parts[3]?.trim()
        if (!value || (availabilityType !== 'AVAILABLE' && availabilityType !== 'UNAVAILABLE')) {
          result.invalidLines.push(line)
        } else {
          const name = parts.slice(4).join(',').trim()
          result.geolocations.push({ ...value, availabilityType, ...(name ? { name } : {}) })
        }
        return result
      },
      { geolocations: [], invalidLines: [] }
    )
  },
  format(values: readonly LeafletGeolocationApiModel[]): string {
    return values
      .map(value => `${formatBase(value)},${value.availabilityType}${value.name ? `,${value.name}` : ''}`)
      .join('\n')
  }
}
